import type { EventDetail, EventsPage, GeocodeRequest } from '@finder/contracts';

/** Why a list request failed. Offline means no HTTP response; server means one came back unusable. */
export type LoadErrorKind = 'offline' | 'server';

/**
 * Failure from `ApiClient.getEvents`. `kind` is the distinction the error card
 * renders (issue #13): a rejected fetch is offline, and a non-OK or unreadable
 * body is a server failure.
 */
export class EventsLoadError extends Error {
  readonly kind: LoadErrorKind;
  readonly status?: number;

  constructor(kind: LoadErrorKind, status?: number) {
    super(
      kind === 'offline'
        ? 'events request failed: offline'
        : `events request failed: ${status ?? 'invalid'}`,
    );
    this.name = 'EventsLoadError';
    this.kind = kind;
    if (status !== undefined) this.status = status;
  }
}

/**
 * Pick the error-card kind. `online === false` wins, because a browser that
 * already knows it is offline should say so even if the thrown error is generic.
 * A null `online` means the runtime has no navigator (tests, SSR).
 */
export function classifyLoadError(error: unknown, online: boolean | null): LoadErrorKind {
  if (online === false) return 'offline';
  if (error instanceof EventsLoadError) return error.kind;
  if (error instanceof TypeError) return 'offline';
  return 'server';
}

/** The configured API base, e.g. "/api/v1" (same-origin) or an absolute URL. */
export const API_BASE: string = import.meta.env?.VITE_API_BASE ?? '/api/v1';

/**
 * Resolve an API-relative path (like the opaque outbound href "/api/v1/out/x")
 * against the configured API base. Same-origin bases return the path unchanged;
 * an absolute base (cross-origin workers.dev) prefixes the API origin so the
 * link still hits the Worker, not the SPA host.
 */
export function apiUrl(apiPath: string): string {
  if (/^https?:\/\//.test(API_BASE)) {
    // API_BASE looks like https://finder-api.<sub>.workers.dev/api/v1
    const origin = new URL(API_BASE).origin;
    return `${origin}${apiPath}`;
  }
  return apiPath; // same-origin
}

/**
 * Thin browser API client for /api/v1. Same-site by default so the opaque
 * outbound path stays same-origin. Never sends visitor location in a URL for
 * geocoding (uses a POST body).
 */
export class ApiClient {
  constructor(
    private readonly baseUrl: string = API_BASE,
    private readonly fetchFn: typeof fetch = fetch.bind(globalThis),
  ) {}

  async getEvents(params: {
    lat: number;
    lng: number;
    radiusMiles?: number;
    windowDays?: number;
    cursor?: string;
  }): Promise<EventsPage> {
    const q = new URLSearchParams();
    q.set('lat', String(params.lat));
    q.set('lng', String(params.lng));
    if (params.radiusMiles !== undefined) q.set('radiusMiles', String(params.radiusMiles));
    if (params.windowDays !== undefined) q.set('windowDays', String(params.windowDays));
    if (params.cursor) q.set('cursor', params.cursor);
    let res: Response;
    try {
      res = await this.fetchFn(`${this.baseUrl}/events?${q.toString()}`);
    } catch {
      throw new EventsLoadError('offline');
    }
    if (!res.ok) throw new EventsLoadError('server', res.status);
    try {
      return (await res.json()) as EventsPage;
    } catch {
      throw new EventsLoadError('server');
    }
  }

  /**
   * One event's description and food-evidence quotes. lat/lng are sent together
   * so the response can include visitor distance; omitted entirely otherwise.
   */
  async getEvent(canonicalId: string, origin?: { lat: number; lng: number }): Promise<EventDetail> {
    const q = new URLSearchParams();
    if (origin) {
      q.set('lat', String(origin.lat));
      q.set('lng', String(origin.lng));
    }
    const qs = q.toString();
    const res = await this.fetchFn(
      `${this.baseUrl}/events/${encodeURIComponent(canonicalId)}${qs ? `?${qs}` : ''}`,
    );
    if (!res.ok) throw new Error(`event detail failed: ${res.status}`);
    return (await res.json()) as EventDetail;
  }

  /** Resolve manual place text. Raw text is sent in the POST body, never a URL. */
  async geocode(placeText: string): Promise<{ lat: number; lng: number } | null> {
    const body: GeocodeRequest = { placeText };
    const res = await this.fetchFn(`${this.baseUrl}/geocode`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 404 || res.status === 422) return null;
    if (!res.ok) throw new Error(`geocode failed: ${res.status}`);
    return (await res.json()) as { lat: number; lng: number };
  }

  /** Best-effort impression submission. Never throws to the caller. */
  async submitImpressions(tokens: string[]): Promise<void> {
    if (tokens.length === 0) return;
    try {
      await this.fetchFn(`${this.baseUrl}/impressions`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tokens }),
        keepalive: true,
      });
    } catch {
      // Telemetry must never affect the UI.
    }
  }
}
